import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  combineLatest,
  first,
  lastValueFrom,
  map,
} from "rxjs";
import { Injectable } from "@angular/core";
import { Storage } from "@ionic/storage-angular";
import { isEqual, cloneDeep } from "lodash-es";
import { DataProvider, SyncData } from "./data-provider.d";
import { Achievement } from "./achievements.data";

import { console, verboseLogger } from "#/log";
import { DEBUG_setUuid as _DEBUG_setUuid, DEBUG_getUuid } from "#/DEBUG_uuid";
import type { SamplesProgress, UserBuild } from "./data.service";
import { Chem } from "#/declarations";

const DEBUG_setUuid = (x: object, uuid?: string | (() => string)) =>
  _DEBUG_setUuid(
    x,
    () =>
      `localdata-${(typeof uuid === "function" ? uuid() : typeof uuid === "string" ? uuid : undefined)?.replace("localdata-", "") ?? performance.now().toFixed(0).padStart(6, "0")}`,
  );

/** Enable extra-verbose logging for this file (while in dev mode) */
const VERBOSE_LOGGING = false;

const verbose = verboseLogger("app.local-data.service", console, {
  enable: VERBOSE_LOGGING,
});

type StorageKeyTypeId =
  | "build"
  | "notes"
  | "ranges"
  | "progress"
  | "achievement"
  | "access"
  | "seen";

@Injectable()
export class LocalDataService implements DataProvider {
  progress!: Observable<Array<Object>>;
  private sampleId!: string;

  protected userId: string | null = null;

  protected _storage: Storage | null = null;
  protected _storageCreated = new BehaviorSubject(false);

  private currentBuildSubject: ReplaySubject<UserBuild | null> =
    new ReplaySubject(1);
  private currentRangesSubject: ReplaySubject<Array<any>> = new ReplaySubject(
    1,
  );
  private currentNotesSubject: ReplaySubject<any> = new ReplaySubject(1);
  private progressSubject: ReplaySubject<SamplesProgress> = new ReplaySubject(
    1,
  );
  private uidSubject: ReplaySubject<string | null> = new ReplaySubject(1);
  private lastSync: number = 0;
  private lastUpdatePromises: {
    ranges: Promise<any>;
    build: Promise<any>;
  } = {
    ranges: Promise.resolve(),
    build: Promise.resolve(),
  };

  protected static _debugAvailableWaitSerial = 0;

  /**
   * Observable for current status of LocalData service (should be equivalent to isAvailable())
   */
  public available: Observable<boolean>;

  /**
   * Loads uid, fetches samples, fetches sync timestamp.
   */
  constructor(private storage: Storage) {
    console.debug("local-data.service: Constructor running.");

    this.progressSubject = new ReplaySubject<SamplesProgress>(1);

    /** XXX DEBUG - tag currentRangesSubject with uuid so we can track it */
    DEBUG_setUuid(this.currentRangesSubject);
    console.debug(
      "local-data.service: constructor, currentRangesSubject uuid:",
      DEBUG_getUuid(this.currentRangesSubject),
    );

    this.initStorageAsync();

    this.uidSubject.subscribe((uid) => {
      this.userId = uid;
    });

    this.available = combineLatest([
      this._storageCreated,
      this.uidSubject,
    ]).pipe(map(([storageCreated, uid]) => storageCreated && uid !== null));
  }

  private async initStorageAsync(): Promise<void> {
    const waitStart = performance.now();
    verbose.debug(`local-data: init, waiting for storage.create()...`);
    this._storage = await this.storage.create();
    verbose.debug(
      `local-data.service: ... storage created (${(performance.now() - waitStart).toPrecision(4)} ms)`,
    );
    this._storageCreated.next(true);
  }

  /**
   * Returns whether the storage is ready or not
   */
  public isAvailable(this: LocalDataService): this is typeof this & {
    _storage: Storage;
    userId: NonNullable<LocalDataService["userId"]>;
  } {
    const haveUserId = Boolean(this.userId);
    const haveStorage = Boolean(this._storage);
    const available = haveUserId && haveStorage;
    if (!available)
      verbose.debug(`local-data service is unavailable:`, {
        haveUserId,
        haveStorage,
      });

    return available;
  }

  /**
   * Emits _only_ when service becomes available.
   * @see {@link LocalDataService.available} for complete status information.
   */
  public get whenAvailable(): Observable<true> {
    return this.available.pipe(
      first((value) => value === true),
      map(() => true),
    );
  }

  /**
   * Wait for async initialization
   */
  public async whenAvailablePromise(this: LocalDataService) {
    return await lastValueFrom(this.whenAvailable);
  }

  protected assertStorageDefined(): asserts this is typeof this & {
    _storage: Storage;
  } {
    if (this._storage === null)
      throw new Error(
        "local-data service unavailable: unexpected null Storage after initialization",
      );
  }

  protected assertUserIdSet(): asserts this is typeof this & {
    userId: string;
  } {
    if (!this.userId)
      throw new Error(
        "local-data service unavailable: unexpected unset userId",
      );
  }

  /**
   * Returns the last synced timestamp
   */
  getLastUpdated(): number {
    return this.lastSync;
  }

  async setLastUpdated(timestamp: number): Promise<void> {
    await this.whenAvailablePromise();

    this.lastSync = timestamp;
    await this.setObject(
      "/users/" + this.userId + "/lastSynced",
      this.lastSync,
    );
  }

  /**
   * Returns the current build observable
   */
  getCurrentBuild(): Observable<UserBuild | null> {
    return this.currentBuildSubject.asObservable();
  }

  /**
   * Returns the current ranges observable.
   */
  getCurrentRanges(): Observable<Array<Object>> {
    const subjUuid = DEBUG_getUuid(this.currentRangesSubject) ?? "[no-uuid]";

    const observable = this.currentRangesSubject.asObservable();

    DEBUG_setUuid(observable, `${subjUuid}-observable`);
    // console.debug(
    //   "local-data.service: getCurrentRanges:",
    //   "currentRangesSubject uuid:",
    //   subjUuid,
    //   "observable uuid:",
    //   DEBUG_getUuid(observable),
    // );

    return observable;
  }

  /**
   * Returns the current notes observable
   */
  getCurrentNotes(): Observable<Object> {
    return this.currentNotesSubject.asObservable();
  }

  getSamplesProgress(): Observable<SamplesProgress> {
    return this.progressSubject.asObservable();
  }

  /**
   * Sets the userId to be used in retrieval and storage. Until this is set,
   * The service will not be available.
   */
  async setUserId(uid: string | null): Promise<void> {
    console.debug("local-data.service: Setting current user id: ", uid);
    this.uidSubject.next(uid);
    if (uid) {
      this.assertUserIdSet();

      await this.whenAvailablePromise();

      if (this._storage === null)
        throw new Error(
          `unexpected null _storage after whenAvailable resolved`,
        );

      await this.fetchObject(`/users/${this.userId}/lastSynced`).then(
        (data) => {
          this.lastSync = <number>data;
        },
      );

      console.debug("local-data.service: fetching samples progress");
      const data = await this.fetchObject<SamplesProgress>(
        this.getKey("progress"),
      );

      this.progressSubject.next(data ?? {});
    }
  }

  /**
   * Sets the current sample. This also loads the relevant build, ranges, and
   * notes for the active sample.
   */
  async setCurrentSample(sampleId: string | null): Promise<void> {
    console.debug(`local-data.service: setting sample '${sampleId}'`);

    if (sampleId === null) {
      console.debug("local-data.service: setCurrentSample: sample is null");
      this.currentBuildSubject.complete();
      this.currentRangesSubject.complete();
      this.currentNotesSubject.complete();
      return;
    }

    console.debug(
      "local-data.service: Initializing sample",
      sampleId,
      "for user id:",
      this.userId,
    );

    this.sampleId = sampleId;

    /**
     * immediately complete and then re-initialize our Subjects before
     * waiting for when-available to complete initialization; ensuring
     * any subsequent subscriptions are to the new Observables.
     *
     * @see note in {AppDataService.setCurrentSample}
     **/
    this.reinitializeSubjectsOnNewSample();

    this.whenAvailable.subscribe(() => this.fetchDataForSubjects());
  }

  /**
   * Update ranges/build/notes subjects by fetchObject() data from storage
   **/
  private async fetchDataForSubjects() {
    if (!this.sampleId) {
      console.debug(
        `local-data.service: fetchDataForSubjects: no sample, nothing to do`,
      );
      return;
    }

    console.debug(
      "local-data.service: now fetching data for ranges/notes/build for sample:",
      this.sampleId,
      "for user id:",
      this.userId,
    );

    await Promise.all([
      this.fetchObject<UserBuild>(this.getKey("build")).then((data) => {
        console.debug(
          "local-data: retrieved build data",
          data,
          (data as any)?.render?.structure,
          "updating currentBuildSubject, uuid:",
          DEBUG_getUuid(this.currentBuildSubject),
        );
        this.currentBuildSubject.next(data);
      }),
      this.fetchObject(this.getKey("ranges")).then((data) => {
        const list = this.makeList(data);
        console.debug(
          "local-data: retrieved range data",
          list,
          "updating currentRangesSubject, uuid:",
          DEBUG_getUuid(this.currentRangesSubject),
        );
        this.currentRangesSubject.next(list);
      }),
      this.fetchObject(this.getKey("notes")).then((data) => {
        this.currentNotesSubject.next({ $value: data });
      }),
    ]);
    console.debug(
      `local-data.service: finished updates for sample '${this.sampleId}'`,
    );
  }

  /**
   * Initialize new ReplaySubjects for current sample's user data
   */
  private reinitializeSubjectsOnNewSample(): void {
    this.currentBuildSubject.complete();
    this.currentNotesSubject.complete();
    this.currentRangesSubject.complete();

    this.currentBuildSubject = new ReplaySubject<UserBuild | null>(1);
    this.currentRangesSubject = new ReplaySubject<Array<Object>>(1);
    /** XXX DEBUG - tag currentRangesSubject with uuid so we can track it */
    DEBUG_setUuid(this.currentRangesSubject);
    console.debug(
      "local-data.service: init currentRangesSubject, uuid:",
      DEBUG_getUuid(this.currentRangesSubject),
    );

    this.currentNotesSubject = new ReplaySubject<Object>(1);

    /** Reset lastUpdatePromises so we're not waiting on stale requests */
    this.lastUpdatePromises.build = Promise.resolve();
    this.lastUpdatePromises.ranges = Promise.resolve();
  }

  /**
   * Sets a type of data (build, ranges, notes) to local storage
   * Guard with `await this.whenAvailablePromise()` before calling.
   */
  protected async setDataOfType<
    DataType extends "build" | "ranges" | "notes",
    T,
  >(type: DataType, data: Record<string, T>): Promise<void> {
    verbose.debug(`local-data: setting ${type}`, data);
    if (data && typeof data === "object") {
      await Promise.all(
        Object.keys(data).map((sampleId) =>
          this.setObject(this.getKey(type, sampleId), data[sampleId]),
        ),
      );
    } else {
      console.warn(`local-data: invalid sync data for ${type}`, data);
    }
  }

  /**
   * Sets all sample progresses from a synced object
   *
   * Guard with `await this.whenAvailablePromise()` before calling.
   */
  protected async setSamplesProgressForSync(
    samples: SamplesProgress,
  ): Promise<void> {
    console.debug("local-data: Setting sample progress from sync", samples);
    if (samples) {
      await this.setObject(this.getKey("progress"), samples);
      this.progressSubject.next(samples);
    }
  }

  /**
   * Sets the build for the current sample
   */
  public async setBuild(build: UserBuild): Promise<void> {
    await this.setObject(this.getKey("build"), build);
    console.debug(
      `local-data.service: updating currentBuildSubject:`,
      build?.render?.structure,
      build,
    );
    this.currentBuildSubject.next(build);
  }

  /**
   * Sets the value of a particular range in the ranges array.
   */
  public async setRange(rangeId: string, data: any): Promise<void> {
    let key = this.getKey("ranges");
    const rangeData: Record<string, any> = (await this.fetchObject(key)) ?? {};

    rangeData[rangeId] = data;
    await this.setObject(key, rangeData);
    this.currentRangesSubject.next(this.makeList(rangeData));
  }

  /**
   * Sets the progress for a particular sampleId
   */
  async setSampleProgress(sampleId: string, progress: number): Promise<void> {
    await this.whenAvailablePromise();

    let key = this.getKey("progress");
    let progressData: SamplesProgress = (await this.fetchObject(key)) ?? {};

    progressData[sampleId] = { progress };
    await this.setObject(key, progressData);
    this.progressSubject.next(progressData);
  }

  /**
   * Sets a specific achievement into local storage
   * @param achievementId id of the achievement to adjust
   * @param progress progress to set
   */
  async setAchievement(
    ach: any,
    percentValue: number = 0,
    progressValue: number = 0,
  ): Promise<boolean> {
    await this.whenAvailablePromise();

    let key = this.getKey("achievement", ach.id);
    let id = ach.id;
    let progress = progressValue; //progressValue === null? ach.progress: ach.progress;// + 1; //wrong for setting all at once
    let percent = percentValue === null ? ach.percent : percentValue;
    let completed = progressValue >= ach.outOf ? true : false;
    let outOf = ach.outOf;
    let title = ach.title;
    let descriptionUncleared = ach.descriptionUncleared;
    let descriptionCleared = ach.descriptionCleared;
    let image = ach.image;
    let undiscovered = ach.undiscovered;
    let keyword = ach.keyword;

    const achievementData = {
      id,
      progress,
      percent,
      completed,
      outOf,
      title,
      descriptionUncleared,
      descriptionCleared,
      image,
      undiscovered,
      keyword,
    };
    console.debug("local-data: achievementData:", achievementData);
    this.setObject(key, achievementData);
    return completed;
  }

  /**
   * Fetches the achievements from local storage, if any
   */
  async getAchievements(ids: Array<string>) {
    await this.whenAvailablePromise();

    const achievements = (
      await Promise.all(
        ids
          .map((id) => this.getKey("achievement", id))
          .map((key) => this.fetchObject<Achievement>(key)),
      )
    ).filter((data): data is NonNullable<typeof data> => data !== null);
    console.debug("local-data: achievements:", achievements);
    return achievements;
  }

  /**
   * Sets the seen value (indicates if the user has seen the thank-you for purchasing alert)
   * This function uses the storage object directly because the key being set is being done
   * before a userid is set. It is a device-specific value, not user, so this is ok.
   */
  async setSeen() {
    this.assertStorageDefined();

    let key = this.getKey("seen");
    await this._storage.set(key, JSON.stringify("true"));
    console.debug("local-data: Added data at", key, "true");
  }

  /**
   * Gets the seen value (indicates if the user has seen the thank-you for purchasing alert)
   */
  public async getSeen(): Promise<boolean> {
    await this.whenAvailablePromise();
    let key = this.getKey("seen");
    const seenData = await this.fetchObject(key);

    let booleanSeenData = seenData === "true";
    return booleanSeenData;
  }

  /**
   * Sets the user as having access to all samples in local storage
   */
  public async setUserAsAllAccess() {
    await this.whenAvailablePromise();
    await this.setObject(this.getKey("access"), "true");
  }

  /**
   * Gets the all access variable from local storage, returns false if it doesnt exist.
   */
  async getUserAllAccess(): Promise<Object> {
    await this.whenAvailablePromise();
    let key = this.getKey("access");
    const accessData = await this.fetchObject(key);
    let booleanAccessData = accessData === "true";
    return booleanAccessData;
  }

  /**
   * Set the notes object in storage and push the new note to its Observable.
   */
  async setNotes(data: Object): Promise<void> {
    await this.setObject(this.getKey("notes"), data);
    this.currentNotesSubject.next({ $value: data });
  }

  /**
   * Discard any inbound feedback data, as this is not retained locally.
   */
  sendFeedback(feedbackData: Object): void {
    console.debug("local-data: Got feedback data. Discarding it.");
  }

  /**
   * Update the build object in local storage and push the new build to its
   * Observable
   */
  public async updateBuild(build: Partial<UserBuild>): Promise<void> {
    await this.whenAvailablePromise();

    await this.lastUpdatePromises.build;

    const doUpdate = async () => {
      const existing = await this.fetchObject<UserBuild>(this.getKey("build"));
      const merged = cloneDeep({ ...existing, ...build });
      const isDifferent = !isEqual(merged, existing);
      console.debug(`local-data.service: updateBuild():`, {
        existing: existing?.render?.structure,
        newBuild: build?.render?.structure,
        merged: merged?.render?.structure,
        isDifferent,
      });
      if (isDifferent) {
        await this.setBuild(merged as UserBuild);
      }
    };
    this.lastUpdatePromises.build = doUpdate();
  }

  /**
   * Update a range in the storage and push the new ranges array to its
   * Observable
   */
  public async updateRange(
    rangeId: string,
    data: Partial<Chem.Range>,
  ): Promise<void> {
    await this.whenAvailablePromise();

    await this.lastUpdatePromises.ranges;

    let key = this.getKey("ranges");
    this.lastUpdatePromises.ranges = this.fetchObject<
      Record<string, Partial<Chem.Range>>
    >(key).then((rangeData) => {
      if (!rangeData) {
        rangeData = {};
      }
      const oldData = rangeData[rangeId];
      rangeData[rangeId] = { ...oldData, ...data };
      this.currentRangesSubject.next(this.makeList(rangeData));
      return this.setObject(key, rangeData);
    });
  }

  /**
   * Reset's the user's local data
   */
  public async reset(): Promise<void> {
    await this.whenAvailablePromise();

    this.assertStorageDefined();
    this.assertUserIdSet();

    const userKeys = await this.getUserKeys();

    await Promise.all(
      userKeys
        .filter(
          (key): key is string =>
            typeof key === "string" && !key.includes("achievement)"),
        )
        .map((key) => this._storage.remove(key)),
    );
    this.progressSubject.next({});
  }

  /**
   * Clears just the achievements
   */
  async resetAchievements(): Promise<void> {
    await this.whenAvailablePromise();

    const userKeys = await this.getUserKeys();
    await Promise.all(
      userKeys
        .filter((key) => key.includes("achievement"))
        .map((key) => this.resetLocalAchievement(key)),
    );
  }

  /**
   * Uses the info in the key to reset the values in storage
   * @param key - key of the achievement in storage
   */
  async resetLocalAchievement(key: string): Promise<void> {
    const achievementData: Partial<Achievement> =
      (await this.fetchObject<Achievement>(key)) ?? {};

    let id = achievementData["id"];
    let progress = 0;
    let percent = 0;
    let completed = false;
    let outOf = achievementData["outOf"];
    let title = achievementData["title"];
    let descriptionUncleared = achievementData["descriptionUncleared"];
    let descriptionCleared = achievementData["descriptionCleared"];
    let image = achievementData["image"];
    let undiscovered = achievementData["undiscovered"];
    let keyword = achievementData["keyword"];

    await this.setObject(key, {
      id,
      progress,
      percent,
      completed,
      outOf,
      title,
      descriptionUncleared,
      descriptionCleared,
      image,
      undiscovered,
      keyword,
    });
  }

  /**
   * Resolves a promise with all of the user data from local storage
   * TODO: the structure of the local storage data makes it difficult to
   *  retrieve all of the user data at once. perhaps restructure so that each
   *  sample has a single key for all of its data.
   */
  async getSyncData(): Promise<SyncData> {
    await this.whenAvailablePromise();

    this.assertUserIdSet();
    let builds: SyncData["builds"] = {};
    let ranges: SyncData["ranges"] = {};
    let notes: SyncData["notes"] = {};
    let progress: SyncData["progress"] = {};

    let promises = [];

    // Progress is pushed separately, since it's a single object key
    promises.push(
      this.fetchObject(this.getKey("progress")).then((data: any) => {
        if (data) {
          delete data.$key;
          delete data.$value;
        }
        progress = data;
      }),
    );

    const userKeys = await this.getUserKeys();
    userKeys.forEach((key: string) => {
      let parsedKey = this.parseKey(key);

      switch (parsedKey?.type) {
        case "user-build":
          promises.push(
            this.fetchObject<UserBuild>(
              this.getKey("build", parsedKey.sampleId),
            ).then((build) => {
              if (build) builds[parsedKey.sampleId] = build;
            }),
          );
          break;

        case "user-ranges":
          promises.push(
            this.fetchObject<Record<string, Chem.Range>>(
              this.getKey("ranges", parsedKey.sampleId),
            ).then((range) => {
              if (range) ranges[parsedKey.sampleId] = range;
            }),
          );
          break;

        case "user-notes":
          promises.push(
            this.fetchObject(this.getKey("notes", parsedKey.sampleId)).then(
              (note) => {
                if (note) notes[parsedKey.sampleId] = note;
              },
            ),
          );
          break;
      }
    });

    await Promise.all(promises);
    return { progress, builds, ranges, notes };
  }

  /**
   * Sets the local storage data with the data from `newData` and updates
   * the timestamp to the provided value
   */
  async setSyncData(newData: SyncData, timestamp: number): Promise<void> {
    await this.whenAvailablePromise();

    await Promise.all([
      this.setDataOfType("build", newData.builds),
      this.setDataOfType("ranges", newData.ranges),
      this.setDataOfType("notes", newData.notes),
      this.setSamplesProgressForSync(newData.progress),
      this.setLastUpdated(timestamp),
    ]);

    // update the state of the current sample, if set
    await this.fetchDataForSubjects();
  }

  /**
   * Parse the storage key for the type, user, and sample
   */
  private parseKey(key: string) {
    let regex = /\/([a-zA-Z\-]+)\/([a-zA-Z0-9\-]+)\/([a-zA-Z\-]+)/g;
    let matches = regex.exec(key);
    if (matches && matches.length === 4) {
      return { type: matches[1], userId: matches[2], sampleId: matches[3] };
    }
    return null;
  }

  /**
   * Get local storage keys for user
   */
  private async getUserKeys(): Promise<string[]> {
    await this.whenAvailablePromise();
    this.assertStorageDefined();
    this.assertUserIdSet();

    const match = new RegExp(this.userId);
    return (await this._storage.keys()).filter((k) => match.test(k));
  }

  /**
   * Helper function to retrieve data keys of different types
   */
  private getKey(type: StorageKeyTypeId, sampleId?: string): string {
    if (!sampleId) {
      sampleId = this.sampleId;
    }

    const assertSampleIdAndUserSet = () => {
      this.assertUserIdSet();
      if (!sampleId)
        throw new Error(`Unexpected unset sampleId retrieving data '${type}'`);
    };

    const _getKey = () => {
      switch (type) {
        case "build":
          assertSampleIdAndUserSet();
          return `/user-build/${this.userId}/${sampleId}`;
        case "notes":
          assertSampleIdAndUserSet();
          return `/user-notes/${this.userId}/${sampleId}`;
        case "ranges":
          assertSampleIdAndUserSet();
          return `/user-ranges/${this.userId}/${sampleId}`;
        case "progress":
          this.assertUserIdSet();
          return `/users/${this.userId}/samples`;
        case "achievement":
          assertSampleIdAndUserSet();
          console.log("assert passed"); //TODO: remove
          return `/users-achievements/${this.userId}/achievement/${sampleId}`;
        case "access":
          return `/user-access/spectrochem/access`;
        case "seen":
          return `/user-seen/spectrochem/seen`;
        default:
          throw new Error(`unrecognized key type specifier '${type}'`);
          return "";
      }
    };

    const key = _getKey();
    verbose.debug(
      `getKey(): userId '${this.userId}', sample '${sampleId}':`,
      key,
    );
    return key;
  }

  /**
   * Take an object of keys and turn it into an array
   */
  private makeList(data: any): Array<any> {
    if (!data) {
      verbose.debug(`makeList:`, data, "[empty]:", []);
      return [];
    }

    const list = Object.keys(data).map((id) => ({ $key: id, ...data[id] }));
    verbose.debug(`makeList:`, data, "as list:", list);
    return list;
  }

  /**
   * Fetch an object from the storage.
   * Returns {T | null}, because localforage getItem() returns null if key not found.
   *
   * Guard with `await this.whenAvailablePromise()` before calling.
   *
   * @see {@link getItem getItem: underlying LocalForage method called by @ionic/storage}
   */
  private async fetchObject<T>(objectPath: string): Promise<T | null> {
    this.assertStorageDefined();

    const data = await this._storage.get(objectPath);
    if (data === null) return null;
    return JSON.parse(data) as T;
  }

  /**
   * Sets an object in local storage after serializing it.
   * This can also be a list.
   *
   * Guard with `await this.whenAvailablePromise()` before calling.
   */
  private async setObject<T>(objectPath: string, data: T): Promise<void> {
    this.assertStorageDefined();

    await this._storage.set(objectPath, JSON.stringify(data));
    verbose.debug("local-data: Added data at", objectPath, data);
  }
}

/** reference for fetchObject documentation */
import type { getItem } from "localforage";
